import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBanner } from '@mylighthouse/prism-ember';
import { getTranslationKeysForBanner } from 'lumen/utils/components/general/empty-state-otb-banner';
import type {
  OTBSSEntryPoint,
  OTBSSViaQPValue,
} from 'lumen/utils/constants/otb-selfservice-constants';

interface GeneralEmptyStateOtbBannerSignature {
  Args: {
    bannerPosition?: 'bottom' | 'island' | 'top';
    isDismissable?: boolean;
    otbSelfServiceEntryPoint: OTBSSEntryPoint;
    otbSelfServiceEntryProduct: OTBSSViaQPValue;
  };
}

export default class GeneralEmptyStateOtbBanner extends Component<GeneralEmptyStateOtbBannerSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare myotbSs: Services['myotb-ss'];

  get bannerContent(): {
    message: string;
    title: string;
  } {
    const { otbSelfServiceEntryPoint } = this.args;

    const translationKeys = getTranslationKeysForBanner(
      otbSelfServiceEntryPoint,
      this.access.hasFeature('otb.market_benchmark_eligible'),
    );

    return {
      message: this.intl.t(translationKeys.message),
      title: translationKeys.title ? this.intl.t(translationKeys.title) : '',
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bannerPrimaryAction() {
    return {
      label: this.intl.t('overview_dashboard.otb_ss.empty_state.button.label'),
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      action: () => {
        this.myotbSs.onOtbSelfServiceButtonClick(
          this.args.otbSelfServiceEntryProduct,
          this.args.otbSelfServiceEntryPoint,
        );
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bannerSecondaryAction() {
    if (this.isDismissable) {
      return {
        label: this.intl.t('general.dismiss'),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        action: () => this.myotbSs.onOtbSelfServiceClose(),
      };
    }
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBannerOneLine() {
    return !this.isDismissable;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBannerVisible() {
    // If banner is dismissible, then we'll check from the OTB SS service if the banner should be visible or not.
    if (this.isDismissable) {
      return this.myotbSs.isOtbSelfServiceUpsellBannerVisible;
    }

    // If banner is not dismissible, then we'll always show it as long as the user has the OTB SS available FF.
    return this.access.hasFeature('otb.empty_placeholder');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDismissable() {
    return this.args.isDismissable ?? true;
  }

  <template>
    {{#if this.isBannerVisible}}
      <PrismBanner
        @bannerMessage={{this.bannerContent.message}}
        @bannerStyle="brand"
        @bannerTitle={{this.bannerContent.title}}
        @hasCta={{true}}
        @isOneLine={{this.isBannerOneLine}}
        @position={{@bannerPosition}}
        @primaryAction={{this.bannerPrimaryAction}}
        @secondaryAction={{this.bannerSecondaryAction}}
      />
    {{/if}}
  </template>
}
